/*
 * @Author: iRuxu
 * @Date: 2022-06-16 18:32:38
 * @LastEditTime: 2022-07-13 18:27:30
 * @Description: 通用请求器
 */

import { installStandardInterceptors } from "./interceptors.js";
import axios from "axios";
import User from "./user.js";
import * as signalR from "@microsoft/signalr";
const { tokenKey } = require("@/settings.js");

const PROXY_ENVS = {
    enable: process.env.VUE_APP_PROXY_ENABLE,
    prefix: process.env.VUE_APP_PROXY_PREFIX,
};

const DEFAULT_SERVICE_API = process.env.VUE_APP_SERVICE_API;

const SERVICE_DOMAINS = {
    next: process.env.VUE_APP_NEXT_API || DEFAULT_SERVICE_API,
    titan: process.env.VUE_APP_TITAN_API || process.env.VUE_APP_TITAN || DEFAULT_SERVICE_API,
    erp: process.env.VUE_APP_ERP_API || DEFAULT_SERVICE_API,
    uc: process.env.VUE_APP_UC_API || DEFAULT_SERVICE_API,
    cms: process.env.VUE_APP_CMS_API || DEFAULT_SERVICE_API,
    admin: process.env.VUE_APP_ADMIN_API || DEFAULT_SERVICE_API,
    bee: process.env.VUE_APP_BEE_API || DEFAULT_SERVICE_API,
    pay: process.env.VUE_APP_PAY_API,
};

const API_NAMESPACE_SERVICES = {
    admin: "admin",
    cms: "cms",
    erp: "erp",
    uc: "uc",
    bee: "bee",
    pay: "pay",
    titan: "titan",
    next: "next",
};

function normalizeUrl(value) {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (trimmed === "/") return "/";

    const cleaned = trimmed.replace(/\/+$/, "");
    if (!cleaned) return "";
    if (/^[a-z][a-z\d+.-]*:\/\//i.test(cleaned) || cleaned.startsWith("/")) return cleaned;
    return `https://${cleaned.replace(/^\/+/, "")}`;
}

function localProxyEnabled(options = {}) {
    if (process.env.NODE_ENV !== "development") return false;
    if (options.proxy === false) return false;
    if (options.proxy === true) return true;

    const raw = String(PROXY_ENVS.enable || "").toLowerCase();
    return !["0", "false", "no", "off"].includes(raw);
}

function localProxyBase(serviceKey) {
    const rawPrefix = PROXY_ENVS.prefix || "/__proxy";
    const prefix = `/${String(rawPrefix).replace(/^\/+/, "").replace(/\/+$/, "")}`;
    return `${prefix}/${serviceKey}`;
}

function resolveBaseURL(serviceKey, options = {}, path = "") {
    const requestServiceKey = resolveRequestServiceKey(serviceKey, path);
    if (localProxyEnabled(options) && requestServiceKey) return localProxyBase(requestServiceKey);
    return normalizeUrl(options.domain || SERVICE_DOMAINS[requestServiceKey] || SERVICE_DOMAINS[serviceKey]);
}

function resolveServiceUrl(serviceKey, path = "", options = {}) {
    const baseURL = resolveBaseURL(serviceKey, options, path);
    const normalizedPath = path ? `/${String(path).replace(/^\/+/, "")}` : "";
    if (baseURL === "/") return normalizedPath || "/";
    return `${baseURL}${normalizedPath}`;
}

function resolveRequestServiceKey(serviceKey, path = "") {
    return getApiNamespaceServiceKey(path) || serviceKey;
}

function getApiNamespaceServiceKey(path = "") {
    const pathname = String(path)
        .replace(/^[a-z][a-z\d+.-]*:\/\/[^/]+/i, "")
        .split("?")[0];
    const match = pathname.match(/^\/?api\/([^/?#]+)/);
    const namespace = match && match[1];
    return API_NAMESPACE_SERVICES[namespace] || "";
}

function isAbsoluteUrl(url = "") {
    return /^[a-z][a-z\d+.-]*:\/\//i.test(String(url));
}

function installServiceInterceptors(ins, serviceKey, options) {
    if (localProxyEnabled(options)) {
        ins.interceptors.request.use(function (config) {
            if (config && config.url && !isAbsoluteUrl(config.url)) {
                config.baseURL = resolveBaseURL(serviceKey, options, config.url);
            }
            return config;
        });
    }

    installStandardInterceptors(ins, options);
}

// cms通用请求接口
function $cms(options) {
    let domain = resolveBaseURL("cms", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "cms", options);

    return ins;
}

// titan通用请求接口
function $titan(options) {
    const domain = resolveBaseURL("titan", options);
    const config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "titan", options);

    return ins;
}

// next接口请求构建器
function $next(options) {
    const domain = resolveBaseURL("next", options);

    const config = {
        withCredentials: true,
        baseURL: domain,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        headers: {},
    };

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "next", options);

    return ins;
}

/**
 * io socket
 *
 * @param {*} url
 * @return {*}
 */
function $ios(url) {
    const socketApiUrl = process.env.VUE_APP_WS_API;

    return new signalR.HubConnectionBuilder()
        .withUrl(`${socketApiUrl}/${url}`, {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            accessTokenFactory: () => User.getToken(),
        })
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (_retryContext) => {
                return 5000;
            },
        })
        .build();
}

/**
 * io socket
 *
 * @param {*} url
 * @return {*}
 */
function $nexts(url) {
    // const socketApiUrl = "http://192.168.5.87:5000/";
    // const socketApiUrl = "https://dev.next.deepberry.cn/";
    const socketApiUrl = process.env.VUE_APP_WS_API;

    return new signalR.HubConnectionBuilder()
        .withUrl(`${socketApiUrl}${url}`, {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            accessTokenFactory: () => User.getToken({ version: 2 }),
        })
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (_retryContext) => {
                return 5000;
            },
        })
        .build();
}

function $pay(options) {
    let domain = resolveBaseURL("pay", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "pay", options);

    return ins;
}

function $erp(options) {
    let domain = resolveBaseURL("erp", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "erp", options);

    return ins;
}

function $uc(options) {
    let domain = resolveBaseURL("uc", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "uc", options);

    return ins;
}

function $admin(options) {
    let domain = resolveBaseURL("admin", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "admin", options);

    return ins;
}

function $bee(options) {
    let domain = resolveBaseURL("bee", options);
    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "bee", options);

    return ins;
}

function $serviceTitan(options) {
    let domain = resolveBaseURL("admin", options);
    // const domain = "http://localhost:20080/";

    let config = {
        // 同时发送cookie和basic auth
        withCredentials: true,
        auth: {
            username: "admin",
            password: (localStorage && localStorage.getItem(tokenKey)) || "",
        },
        baseURL: domain,
        headers: {},
    };

    if (options && options.progress) {
        config.onUploadProgress = options.progress;
    }

    // 创建实例
    const ins = axios.create(config);

    // 指定拦截器
    installServiceInterceptors(ins, "admin", options);

    return ins;
}

export {
    $cms,
    $titan,
    $next,
    $ios,
    $nexts,
    $pay,
    $erp,
    $uc,
    $admin,
    $bee,
    $serviceTitan,
    localProxyEnabled,
    resolveBaseURL,
    resolveServiceUrl,
};
